<template>
  <b-container class="public-appointment-container">
    <b-skeleton-wrapper :loading="loading">
      <template #loading>
        <b-card>
          Name: <b-skeleton width="85%"></b-skeleton>
          <b-skeleton width="55%"></b-skeleton>
          <b-skeleton width="75%"></b-skeleton>
        </b-card>
      </template>
      <h3>Name: {{ward.ward_name}}, {{ward.ward_city}}</h3>
      <h4>Book an appointment</h4>
    <b-card>
      <b-alert v-if="errorMessage" variant="danger" dismissible @dismissed="errorMessage = null">
        {{ errorMessage }}
      </b-alert>
      <b-alert v-if="successMessage" variant="success" show>
        {{ successMessage }}
      </b-alert>
      
      <!-- Step 1: Appointment Type & Date/Time Selection -->
      <div v-if="currentStep === 1">
        <!-- Show helpful message when no type is selected -->
        <b-alert v-if="!appointment.appointment_type" variant="info" show class="mb-3">
          Please select an appointment type below to see available dates and times.
        </b-alert>

        <!-- First: Select Appointment Type -->
        <b-form-row>
          <b-col>
            <b-form-group
              id="input-group-type"
              label="Appointment Type:"
              label-for="input-type"
              description="Select the reason for your appointment"
            >
              <b-form-select
                id="input-type"
                v-model="appointment.appointment_type"
                :options="appointmentTypeOptions"
                required
              >
                <template #first>
                  <option :value="null" disabled>-- Please select an appointment type --</option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-form-row>

        <!-- Show "Other Reason" field if OTHER is selected -->
        <b-form-row v-if="showOtherReason">
          <b-col>
            <b-form-group
              id="input-group-other"
              label="Other Reason Description"
              label-for="input-other"
              description="Please describe the reason for your appointment"
            >
              <b-form-input
                id="input-other"
                v-model="appointment.appointment_other_reason"
                type="text"
                placeholder="Describe the reason for your appointment"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
        </b-form-row>

        <!-- Second: Select Date/Time (only show when type is selected) -->
        <b-form-row v-if="appointment.appointment_type">
          <b-col>
            <b-form-group
              id="input-group-1"
              label="When would you like to schedule this appointment?"
              label-for="input-1"
              description="First select a date, then choose a time from the available slots below"
            >
              <div class="date-picker-wrapper">
                <div class="d-flex align-items-center">
                <VueCtkDateTimePicker
                  id="input-group-1"
                  label="Select Appointment Date"
                  :format="timeFormat"
                  :formatted="'dddd, MMMM Do YYYY'"
                  :minDate="dynamicMinDate"
                  :maxDate="dynamicMaxDate"
                  v-model="appointment.appointment_date"
                  :disabled-weekly="disabledDays"
                  :only-date="true"
                  :only-time="false"
                  :no-button="false"
                  :auto-close="true"
                />
                  <b-button
                    variant="outline-secondary"
                    class="ml-2"
                    @click="toggleTimeFormat"
                    size="sm"
                  >
                    {{ show12HourFormat ? '24h' : '12h' }}
                  </b-button>
                </div>
              </div>
              <div v-if="loadingAvailableSlots" class="mt-2">
                <small class="text-info">
                  <b-spinner small></b-spinner> Checking availability...
                </small>
              </div>
              <div v-else-if="appointment.appointment_date && selectedDayHours.length > 0" class="mt-2">
                <small class="text-muted d-block mb-2">
                  Available times for selected day - click to select:
                </small>
                <div class="d-flex flex-wrap gap-2">
                  <b-badge
                    v-for="timeSlot in selectedDayHours"
                    :key="timeSlot"
                    :variant="isTimeSelected(timeSlot) ? 'primary' : 'outline-primary'"
                    pill
                    class="time-slot-pill"
                    @click="selectTimeSlot(timeSlot)"
                  >
                    {{ formatTimeSlot(timeSlot) }}
                  </b-badge>
                </div>
              </div>
              <div v-else-if="appointment.appointment_date && selectedDayHours.length === 0" class="mt-2">
                <small class="text-warning">
                  No available times for this day. Please select another day.
                </small>
              </div>
              <div v-else class="mt-2">
                <small class="text-muted">
                  Select a date to see available times
                </small>
              </div>
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-row>
          <b-col>
            <b-button
              type="button"
              variant="primary"
              @click="goToStep2"
              :disabled="!appointment.appointment_date || !appointment.appointment_type"
            >
              Next
            </b-button>
          </b-col>
        </b-form-row>
      </div>

      <!-- Step 2: Contact Information -->
      <div v-if="currentStep === 2">
        <!-- Selected Date/Time & Type Summary -->
        <b-alert variant="info" show class="mb-4">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <strong>Selected Appointment:</strong><br>
              <span v-if="formattedSelectedDateTime">{{ formattedSelectedDateTime }}</span>
              <span v-else>No date/time selected</span>
              <br>
              <strong>Type:</strong> {{ getAppointmentTypeDescription(appointment.appointment_type) }}
              <span v-if="showOtherReason && appointment.appointment_other_reason">
                - {{ appointment.appointment_other_reason }}
              </span>
            </div>
            <b-button variant="outline-primary" size="sm" @click="goToStep1">
              Change
            </b-button>
          </div>
        </b-alert>
        
        <b-form @submit="onSubmit">
          <!-- Phone Number Input (First Field) -->
          <b-form-row>
            <b-col>
              <b-form-group
                id="input-group-phone"
                label="Phone Number:"
                label-for="input-phone"
                description="Enter your phone number to look up your contact information"
              >
                <b-input-group>
                  <b-form-input
                    id="input-phone"
                    v-model="attendee.phone_number"
                    type="tel"
                    placeholder="Enter your phone number"
                    required
                    @input="onPhoneNumberChange"
                  ></b-form-input>
                  <b-input-group-append v-if="phoneLookupLoading">
                    <b-spinner small></b-spinner>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-form-row>

          <!-- Contact Found Display -->
          <b-form-row v-if="contactFound && !showNameInputs">
            <b-col>
              <b-alert variant="success" show>
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Contact Found:</strong> {{ contactFound.first_name }} {{ contactFound.last_name }}
                  </div>
                  <b-button variant="outline-secondary" size="sm" @click="clearContactLookup">
                    Change
                  </b-button>
                </div>
              </b-alert>
            </b-col>
          </b-form-row>

          <!-- Contact Not Found Message -->
          <b-form-row v-if="!contactFound && showNameInputs && attendee.phone_number">
            <b-col>
              <b-alert variant="info" show>
                <p class="mb-2">Contact not found. Please enter your name below to continue booking.</p>
                <p class="mb-0">
                  Want to receive text messages from your ward? 
                  <a :href="publicSignupUrl" target="_blank" rel="noopener noreferrer">Sign up here</a>
                </p>
              </b-alert>
            </b-col>
          </b-form-row>

          <!-- Name Input Fields (shown if contact not found or user clicked Change) -->
          <b-form-row v-if="showNameInputs">
            <b-col>
              <b-form-group
                id="input-group-first-name"
                label="First Name:"
                label-for="input-first-name"
                description="Your first name"
              >
                <b-form-input
                  id="input-first-name"
                  v-model="attendee.first_name"
                  type="text"
                  placeholder="Enter your first name"
                  required
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group
                id="input-group-last-name"
                label="Last Name:"
                label-for="input-last-name"
                description="Your last name"
              >
                <b-form-input
                  id="input-last-name"
                  v-model="attendee.last_name"
                  type="text"
                  placeholder="Enter your last name"
                  required
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-form-row>
        <b-form-row>
          <b-col>
            <b-form-group
              id="input-group-3"
              label="Comments"
              label-for="input-3"
              description="Any comments regarding the appointment"
            >
              <b-form-input
                id="input-3"
                v-model="appointment.comments"
                type="text"
                placeholder="Enter any comments that need to be conveyed"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-form-row>
          <b-col>
            <b-button
                type="button"
                variant="secondary"
                @click="goToStep1"
                class="mr-2"
              >
                Back
              </b-button>
            <b-button
              type="submit"
              variant="primary"
                :disabled="submitting"
              >
                {{ submitting ? 'Submitting...' : 'Submit Appointment' }}
              </b-button>
          </b-col>
        </b-form-row>
      </b-form>
      </div>
    </b-card>
    </b-skeleton-wrapper>
  </b-container>
</template>
<script>
import { AppointmentApi } from "@/app/appointments/appointmentApi";
import { WardApi } from "@/app/wards/wardApi";
import handleError from "@/errorHandler";
import _ from "lodash";

export default {
  name: "PublicBookAppointment",
  props: {
    wardId: {
      type: String,
      required: true
    },
    publicSecurityKey: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      currentStep: 1,
      timeFormat: 'YYYY-MM-DDTHH:mm:ssZ', // ISO format with UTC timezone (always 24h for data)
      appointment: {
        appointment_date: null,
        minutes_duration: 15,
        comments: null,
        appointment_type: null,
        appointment_other_reason: null,
        appointment_location: "church building",
        attendee_contacts: []
      },
      attendee: {
        first_name: null,
        last_name: null,
        phone_number: null
      },
      appointmentTypeOptions: [],
      appointmentDaysOptions: [],
      allAvailableDays: [], // Store all available days (without type filtering)
      appointmentHours: {},
      appointmentDefaults: {}, // Store appointment defaults per type
      disabledHours: [],
      validTimesForSelectedDay: [], // Store valid times for the selected day for validation
      availableSlotsForSelectedDay: [], // Store dynamically available slots (not fully booked)
      loadingAvailableSlots: false, // Loading state for available slots
      errorMessage: null,
      successMessage: null,
      submitting: false,
      show12HourFormat: true,
      wardTimezone: null,
      loading: true,
      minuteInterval: 15, // Dynamic minute interval based on appointment type
      ward: {
        ward_name: null,
        ward_city: null
      },
      isUpdatingDate: false, // Flag to prevent infinite loops
      cachedMinDate: null, // Cache for dynamicMinDate to prevent unnecessary recalculations
      cachedMaxDate: null, // Cache for dynamicMaxDate to prevent unnecessary recalculations
      phoneLookupLoading: false,
      phoneLookupError: null,
      contactFound: null, // stores found contact info
      showNameInputs: false // controls visibility of name fields
    };
  },
  computed: {
    scheduleOnlyFuture() {
      // Return current time in ISO format for minDate
      return this.$moment().format('YYYY-MM-DDTHH:mm:ssZ');
    },
    dynamicMinDate() {
      // Return a dynamic minDate that restricts to allowed times
      // IMPORTANT: This should only restrict time, not date selection
      // For date selection, we use disabled-weekly instead
      // minDate is used to prevent selecting past times, but should allow any enabled day
      
      // Always return current time as minimum to prevent past selections
      // The actual day restrictions are handled by disabled-weekly prop
      return this.scheduleOnlyFuture;
    },
    showOtherReason() {
      return this.appointment.appointment_type === "OTHER";
    },
    disabledDays() {
      // If no days are configured, disable all days
      if (!this.appointmentDaysOptions || this.appointmentDaysOptions.length === 0) {
        return [0, 1, 2, 3, 4, 5, 6];
      }
      const allDays = [0, 1, 2, 3, 4, 5, 6];
      // Use the filter method to find all days *not* in the enabledDays array
      // appointmentDaysOptions contains the day indices (0-6) that are enabled
      return allDays.filter(day => !this.appointmentDaysOptions.includes(day));
    },
    selectedDayHours() {
      // Return dynamically available slots if loaded, otherwise return configured hours
      if (this.availableSlotsForSelectedDay.length > 0) {
        return this.availableSlotsForSelectedDay;
      }
      
      // Fallback to configured hours if dynamic slots not loaded
      if (!this.appointment.appointment_date) {
        return [];
      }
      // Get day name from selected date
      const date = this.$moment(this.appointment.appointment_date);
      const dayIndex = date.day(); // 0 = Sunday, 1 = Monday, etc.
      const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
      const dayName = dayNames[dayIndex];
      
      return this.appointmentHours[dayName] || [];
    },
    displayFormat() {
      // Return format string based on 12-hour/24-hour toggle
      // LLLL is moment's long format, but we can customize it
      if (this.show12HourFormat) {
        // 12-hour format with AM/PM
        return 'dddd, MMMM Do YYYY, h:mm A';
      } else {
        // 24-hour format
        return 'dddd, MMMM Do YYYY, HH:mm';
      }
    },
    formattedSelectedDateTime() {
      // Format the selected date/time for display in step 2 summary
      if (!this.appointment.appointment_date) {
        return null;
      }
      
      try {
        // Parse the appointment date - the date picker gives us what the user selected
        // We'll format it using the same format preference the user has set
        const momentDate = this.$moment(this.appointment.appointment_date);
        
        if (!momentDate.isValid()) {
          return null;
        }
        
        // Format based on user's 12/24 hour preference (same as displayFormat)
        if (this.show12HourFormat) {
          return momentDate.format('dddd, MMMM Do YYYY, h:mm A');
        } else {
          return momentDate.format('dddd, MMMM Do YYYY, HH:mm');
        }
      } catch (e) {
        console.error('Error formatting date:', e);
        return null;
      }
    },
    publicSignupUrl() {
      // Generate public signup URL
      const publicUrl = `${location.protocol}//${location.host}`;
      return `${publicUrl}/ward/${this.wardId}/public/signup/${this.publicSecurityKey}`;
    },
    dynamicMaxDate() {
      // Don't restrict maxDate - let users select any future date
      // The time restrictions are handled by disabled-hours and validation
      // Returning null means no max date restriction
      return null;
    }
  },
  watch: {
    'appointment.appointment_type'(newType, oldType) {
      if (!newType || newType === oldType) {
        return;
      }
      
      // Clear selected date when type changes
      if (oldType) {
        this.appointment.appointment_date = null;
        this.availableSlotsForSelectedDay = [];
      }
      
      // Update available days based on type restrictions
      this.updateAvailableDaysForType(newType);
      
      // Update minute interval based on configured duration
      this.updateMinuteIntervalForType(newType);
      
      // Set next available date/time if we cleared the date
      if (oldType) {
        this.$nextTick(() => {
          this.setNextAvailableDateTime();
        });
      }
    },
    'appointment.appointment_date'(newDate, oldDate) {
      if (!newDate) {
        this.availableSlotsForSelectedDay = [];
        return;
      }
      
      // Prevent infinite loops by checking if the date actually changed
      if (oldDate && this.$moment(newDate).isSame(this.$moment(oldDate))) {
        return;
      }
      
      // Clear cache when day changes to ensure dynamicMinDate recalculates correctly
      const dayChanged = !oldDate || !this.$moment(newDate).isSame(this.$moment(oldDate), 'day');
      if (dayChanged) {
        this.cachedMinDate = null; // Clear cache when switching days
        // Fetch available slots for the new day
        this.fetchAvailableSlotsForSelectedDay();
      }
      
      // When date changes (different day), set to earliest available time for that day
      if (dayChanged) {
        this.setEarliestTimeForSelectedDate();
      }
      
      // Update disabled hours first
      this.updateDisabledHours();
      
      // Then validate and correct the selected time to match allowed times
      // Use $nextTick to ensure the picker has updated before validation
      this.$nextTick(() => {
        if (!this.isUpdatingDate) {
          this.validateAndCorrectSelectedTime();
        }
      });
    }
  },
  async mounted() {
    await this.fetchWardDetails();
    await this.loadSettings();
    this.loading = false;
  },
  methods: {
    async fetchWardDetails() {
      try {
        const { data } = await WardApi.getWardPublic(this.wardId, this.publicSecurityKey);
        this.ward = data.ward;
      } catch (e) {
        handleError(e, "Error loading ward information");
      }
    },
    async loadSettings() {
      try {
        const { data } = await AppointmentApi.getPublicAppointmentSettings(
          this.wardId,
          this.publicSecurityKey
        );
        this.appointmentTypeOptions = data.public_appointments_available.map(opt => ({
          value: opt.appointment_type,
          text: opt.description
        }));
        
        // Store appointment defaults for filtering
        this.appointmentDefaults = data.appointment_defaults || {};
        
        // Set flag before updating any reactive data to prevent watch from triggering
        this.isUpdatingDate = true;
        
        // Clear cache when updating settings
        this.cachedMinDate = null;
        this.cachedMaxDate = null;
        
        // Get appointment hours - days are now always derived from hours in the backend
        this.appointmentHours = data.public_appointment_hours || {};
        this.wardTimezone = data.ward_timezone || null;
        
        // Derive days from hours configuration (backend also does this, but we need it for frontend logic)
        // public_ward_appointment_days is an array of day indices (0-6) from the backend
        // where 0=Sunday, 1=Monday, etc.
        let rawDays = data.public_ward_appointment_days || [];
        
        // Process the raw days data - backend returns array of indices (0-6)
        if (Array.isArray(rawDays) && rawDays.length > 0) {
          this.allAvailableDays = rawDays
            .map(day => {
              if (typeof day === 'number') {
                return day;
              } else if (typeof day === 'string') {
                const parsed = parseInt(day, 10);
                return isNaN(parsed) ? null : parsed;
              }
              return null;
            })
            .filter(index => index !== null && index >= 0 && index <= 6);
        } else {
          // Fallback: derive from hours if backend didn't provide days
          const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
          const daysWithHours = Object.keys(this.appointmentHours).filter(dayName => {
            const hours = this.appointmentHours[dayName];
            return hours && Array.isArray(hours) && hours.length > 0;
          });
          
          this.allAvailableDays = daysWithHours.map(dayName => {
            const index = dayNames.indexOf(dayName);
            return index >= 0 ? index : null;
          }).filter(index => index !== null && index >= 0 && index <= 6);
        }
        
        // Ensure allAvailableDays is a proper array of unique numbers
        this.allAvailableDays = [...new Set(this.allAvailableDays)].sort((a, b) => a - b);
        
        // Initially set appointmentDaysOptions to all available days (no type selected yet)
        this.appointmentDaysOptions = [...this.allAvailableDays];
        
        // Use $nextTick to batch all updates and prevent render loop
        await this.$nextTick();
        
        console.log('Loaded settings:', {
          public_ward_appointment_days: data.public_ward_appointment_days,
          allAvailableDays: this.allAvailableDays,
          appointmentDaysOptions: this.appointmentDaysOptions,
          appointmentDefaults: this.appointmentDefaults,
          appointmentHours: this.appointmentHours,
          wardTimezone: this.wardTimezone,
          disabledDays: this.disabledDays,
          disabledDaysLength: this.disabledDays ? this.disabledDays.length : 0
        });
        
        // Don't set initial date yet - wait for user to select appointment type
        this.updateDisabledHours();
        
        // Reset flag after all updates are complete
        await this.$nextTick();
        this.isUpdatingDate = false;
      } catch (e) {
        handleError(e, "Error loading appointment settings");
        this.errorMessage = "Unable to load appointment settings. Please check the URL and try again.";
        this.isUpdatingDate = false; // Reset flag on error
      }
    },
    validateAndCorrectSelectedTime() {
      // Validate that the selected time matches one of the allowed times for the selected day
      if (!this.appointment.appointment_date || this.isUpdatingDate) {
        return;
      }
      
      const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
      const date = this.$moment(this.appointment.appointment_date);
      const dayIndex = date.day();
      const dayName = dayNames[dayIndex];
      
      // Get available hours for this day
      const availableHours = this.appointmentHours[dayName] || [];
      
      if (availableHours.length === 0) {
        return;
      }
      
      // Get current time (in ward timezone if available)
      let now = this.$moment();
      if (this.wardTimezone && this.$moment.tz) {
        now = this.$moment.tz(this.wardTimezone);
      }
      
      // Check if selected date is today
      const isToday = date.isSame(now, 'day');
      
      // Get the selected time in HH:mm format (rounded to nearest 15 minutes)
      const selectedHour = date.hour();
      const selectedMinute = date.minute();
      const roundedMinute = Math.round(selectedMinute / 15) * 15;
      const selectedTime = `${String(selectedHour).padStart(2, '0')}:${String(roundedMinute).padStart(2, '0')}`;
      
      // Filter out past times if today to get valid times
      const validTimes = availableHours.filter(time => {
        if (isToday) {
          const [hour, minute] = time.split(':').map(Number);
          const timeMoment = date.clone().hour(hour).minute(minute).second(0);
          return timeMoment.isAfter(now);
        }
        return true;
      });
      
      if (validTimes.length === 0) {
        return;
      }
      
      // Check if the selected time (rounded) is in the allowed list
      const isAllowed = validTimes.includes(selectedTime);
      
      // If not allowed, find the nearest allowed time
      if (!isAllowed) {
        // Filter out past times if today
        const validTimes = availableHours.filter(time => {
          if (isToday) {
            const [hour, minute] = time.split(':').map(Number);
            const timeMoment = date.clone().hour(hour).minute(minute).second(0);
            return timeMoment.isAfter(now);
          }
          return true;
        });
        
        if (validTimes.length > 0) {
          // Find the closest allowed time to the selected time
          let closestTime = validTimes[0];
          let minDiff = Math.abs(this.timeToMinutes(selectedTime) - this.timeToMinutes(closestTime));
          
          for (const time of validTimes) {
            const diff = Math.abs(this.timeToMinutes(selectedTime) - this.timeToMinutes(time));
            if (diff < minDiff) {
              minDiff = diff;
              closestTime = time;
            }
          }
          
          // Update the appointment date with the corrected time
          const [hour, minute] = closestTime.split(':').map(Number);
          const correctedDateTime = date.clone().hour(hour).minute(minute).second(0).millisecond(0);
          const correctedTimeStr = correctedDateTime.format('YYYY-MM-DDTHH:mm:ssZ');
          if (correctedTimeStr !== this.appointment.appointment_date) {
            this.isUpdatingDate = true;
            this.appointment.appointment_date = correctedTimeStr;
            this.$nextTick(() => {
              this.isUpdatingDate = false;
            });
          }
        }
      } else if (isToday) {
        // Even if the time is in the allowed list, make sure it's in the future
        if (date.isBefore(now)) {
          // Find the next available time
          const validTimes = availableHours
            .map(time => {
              const [hour, minute] = time.split(':').map(Number);
              return date.clone().hour(hour).minute(minute).second(0);
            })
            .filter(timeMoment => timeMoment.isAfter(now))
            .sort((a, b) => a.diff(b));
          
          if (validTimes.length > 0) {
            const newTimeStr = validTimes[0].format('YYYY-MM-DDTHH:mm:ssZ');
            if (newTimeStr !== this.appointment.appointment_date) {
              this.isUpdatingDate = true;
              this.appointment.appointment_date = newTimeStr;
              this.$nextTick(() => {
                this.isUpdatingDate = false;
              });
            }
          }
        }
      }
    },
    timeToMinutes(timeStr) {
      // Convert HH:mm to minutes since midnight
      const [hour, minute] = timeStr.split(':').map(Number);
      return hour * 60 + minute;
    },
    setEarliestTimeForSelectedDate() {
      // Set the appointment time to the earliest available time for the selected date
      if (!this.appointment.appointment_date) {
        return;
      }
      
      const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
      const date = this.$moment(this.appointment.appointment_date);
      const dayIndex = date.day(); // 0 = Sunday, 1 = Monday, etc.
      const dayName = dayNames[dayIndex];
      
      // Get available hours for this day
      const availableHours = this.appointmentHours[dayName] || [];
      
      if (availableHours.length === 0) {
        // No hours available for this day
        return;
      }
      
      // Get current time (in ward timezone if available)
      let now = this.$moment();
      if (this.wardTimezone && this.$moment.tz) {
        now = this.$moment.tz(this.wardTimezone);
      }
      
      // Check if selected date is today
      const isToday = date.isSame(now, 'day');
      
      // Sort available hours
      const sortedHours = availableHours.sort();
      
      // Find the earliest available time that's in the future (if today) or any time (if future day)
      for (const hourStr of sortedHours) {
        const [hour, minute] = hourStr.split(':').map(Number);
        const candidateDateTime = date.clone().hour(hour).minute(minute).second(0).millisecond(0);
        
        // If today, make sure the time is in the future
        if (isToday) {
          if (candidateDateTime.isAfter(now)) {
            // Found earliest available future time
            const newTimeStr = candidateDateTime.format('YYYY-MM-DDTHH:mm:ssZ');
            if (newTimeStr !== this.appointment.appointment_date) {
              this.isUpdatingDate = true;
              this.appointment.appointment_date = newTimeStr;
              this.$nextTick(() => {
                this.isUpdatingDate = false;
              });
            }
            return;
          }
        } else {
          // Future day, use earliest available time
          const newTimeStr = candidateDateTime.format('YYYY-MM-DDTHH:mm:ssZ');
          if (newTimeStr !== this.appointment.appointment_date) {
            this.isUpdatingDate = true;
            this.appointment.appointment_date = newTimeStr;
            this.$nextTick(() => {
              this.isUpdatingDate = false;
            });
          }
          return;
        }
      }
      
      // If no available time found (e.g., all times are in the past for today), leave as is
    },
    setNextAvailableDateTime() {
      // Find the next available date/time based on ward settings
      if (!this.appointmentDaysOptions || this.appointmentDaysOptions.length === 0) {
        // No days available
        return;
      }
      
      const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
      let currentDate = this.$moment();
      
      // If ward timezone is set, work in ward timezone
      if (this.wardTimezone && this.$moment.tz) {
        currentDate = this.$moment.tz(this.wardTimezone);
      }
      
      // Start from current date, check up to 8 weeks ahead
      for (let weekOffset = 0; weekOffset < 8; weekOffset++) {
        for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
          const checkDate = currentDate.clone().add(weekOffset * 7 + dayOffset, 'days');
          const dayIndex = checkDate.day(); // 0 = Sunday, 1 = Monday, etc.
          
          // Check if this day is enabled
          if (!this.appointmentDaysOptions.includes(dayIndex)) {
            continue;
          }
          
          const dayName = dayNames[dayIndex];
          const availableHours = this.appointmentHours[dayName] || [];
          
          if (availableHours.length === 0) {
            continue;
          }
          
          // Sort available hours
          const sortedHours = availableHours.sort();
          
          // Check each available hour for this day
          for (const hourStr of sortedHours) {
            const [hour, minute] = hourStr.split(':').map(Number);
            const candidateDateTime = checkDate.clone().hour(hour).minute(minute).second(0).millisecond(0);
            
            // If this is today, make sure the time is in the future
            if (candidateDateTime.isSame(currentDate, 'day')) {
              if (candidateDateTime.isAfter(currentDate)) {
                // Found next available time
                this.isUpdatingDate = true;
                this.appointment.appointment_date = candidateDateTime.format('YYYY-MM-DDTHH:mm:ssZ');
                this.updateDisabledHours();
                this.$nextTick(() => {
                  this.isUpdatingDate = false;
                });
                return;
              }
            } else if (candidateDateTime.isAfter(currentDate)) {
              // Found next available time on a future day
              this.isUpdatingDate = true;
              this.appointment.appointment_date = candidateDateTime.format('YYYY-MM-DDTHH:mm:ssZ');
              this.updateDisabledHours();
              this.$nextTick(() => {
                this.isUpdatingDate = false;
              });
              return;
            }
          }
        }
      }
      
      // If no available time found, leave it null
    },
    updateDisabledHours() {
      // If no date selected, don't restrict hours yet (will be restricted when date is selected)
      if (!this.appointment.appointment_date) {
        // Don't disable any hours until a date is selected
        this.disabledHours = [];
        return;
      }
      
      // Get day name from selected date
      const date = this.$moment(this.appointment.appointment_date);
      const dayIndex = date.day(); // 0 = Sunday, 1 = Monday, etc.
      const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
      const dayName = dayNames[dayIndex];
      
      // Get available hours for this day
      const availableHours = this.appointmentHours[dayName] || [];
      
      if (availableHours.length === 0) {
        // If no hours available for this day, disable all hours
        this.disabledHours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
        return;
      }

      // Get current time (in ward timezone if available)
      let now = this.$moment();
      if (this.wardTimezone && this.$moment.tz) {
        now = this.$moment.tz(this.wardTimezone);
      }
      
      // Check if selected date is today
      const isToday = date.isSame(now, 'day');
      
      // Convert available hours to hour numbers, filtering out past times if today
      // Only enable hours that have at least one valid time slot
      const availableHourNumbers = new Set();
      const validTimes = [];
      
      availableHours.forEach(hour => {
        const parts = hour.split(':');
        if (parts.length >= 2) {
          const hourNum = parseInt(parts[0], 10);
          const minuteNum = parseInt(parts[1], 10);
          
          // If today, check if this time is in the future
          if (isToday) {
            const timeMoment = date.clone().hour(hourNum).minute(minuteNum).second(0);
            if (timeMoment.isAfter(now)) {
              availableHourNumbers.add(String(hourNum).padStart(2, '0'));
              validTimes.push(hour);
            }
          } else {
            // Future day, all available hours are valid
            availableHourNumbers.add(String(hourNum).padStart(2, '0'));
            validTimes.push(hour);
          }
        }
      });
      
      // Store valid times for this day for validation
      this.validTimesForSelectedDay = validTimes;
      
      // Calculate disabled hours: all hours (00-23) minus available hours
      // Format must be strings like '00', '01', '02', etc.
      const allHours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
      this.disabledHours = allHours.filter(hour => !availableHourNumbers.has(hour));
      
      console.log('Day:', dayName, 'Available times:', availableHours, 'Valid times:', validTimes, 'Available hour numbers:', Array.from(availableHourNumbers), 'Disabled hours:', this.disabledHours, 'Is today:', isToday);
    },
    toggleTimeFormat() {
      // Toggle display format (the actual format sent to server is always ISO with UTC)
      this.show12HourFormat = !this.show12HourFormat;
    },
    updateAvailableDaysForType(appointmentType) {
      // Get defaults for this appointment type
      const typeDefaults = this.appointmentDefaults[appointmentType];
      
      if (typeDefaults && typeDefaults.optional_available_days && 
          typeDefaults.optional_available_days.length > 0) {
        // This type has day restrictions - filter to only those days
        const dayMap = {
          'SUNDAY': 0, 'MONDAY': 1, 'TUESDAY': 2, 'WEDNESDAY': 3,
          'THURSDAY': 4, 'FRIDAY': 5, 'SATURDAY': 6
        };
        
        this.appointmentDaysOptions = typeDefaults.optional_available_days
          .map(day => dayMap[day])
          .filter(idx => idx !== undefined);
      } else {
        // No restrictions - show all days with configured hours
        this.appointmentDaysOptions = [...this.allAvailableDays];
      }
      
      console.log('Updated available days for type:', appointmentType, this.appointmentDaysOptions);
    },
    updateMinuteIntervalForType(appointmentType) {
      // Get configured duration for this type
      const typeDefaults = this.appointmentDefaults[appointmentType];
      
      if (typeDefaults && typeDefaults.appointment_lengths && 
          typeDefaults.appointment_lengths[appointmentType]) {
        const duration = typeDefaults.appointment_lengths[appointmentType];
        this.appointment.minutes_duration = duration;
        
        // Update date picker interval
        // Use 5-minute intervals for durations <= 15, otherwise use GCD approach
        if (duration <= 15) {
          this.minuteInterval = 5;
        } else if (duration === 30) {
          this.minuteInterval = 15;
        } else if (duration === 45) {
          this.minuteInterval = 15;
        } else if (duration === 60) {
          this.minuteInterval = 30;
        } else {
          this.minuteInterval = 15; // Default
        }
      } else {
        // Use default
        this.appointment.minutes_duration = 15;
        this.minuteInterval = 15;
      }
      
      console.log('Updated minute interval for type:', appointmentType, {
        duration: this.appointment.minutes_duration,
        interval: this.minuteInterval
      });
    },
    getAppointmentTypeDescription(appointmentType) {
      // Get the description for an appointment type
      const option = this.appointmentTypeOptions.find(opt => opt.value === appointmentType);
      return option ? option.text : appointmentType;
    },
    formatTimeSlot(timeSlot) {
      // Format time slot for display based on 12/24 hour preference
      if (!timeSlot) return '';
      
      const [hour, minute] = timeSlot.split(':').map(Number);
      
      if (this.show12HourFormat) {
        // 12-hour format
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
        return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
      } else {
        // 24-hour format
        return timeSlot;
      }
    },
    isTimeSelected(timeSlot) {
      // Check if this time slot is currently selected
      if (!this.appointment.appointment_date || !timeSlot) {
        return false;
      }
      
      const selectedDate = this.$moment(this.appointment.appointment_date);
      const [slotHour, slotMinute] = timeSlot.split(':').map(Number);
      
      return selectedDate.hour() === slotHour && selectedDate.minute() === slotMinute;
    },
    selectTimeSlot(timeSlot) {
      // Set the appointment date to the selected time slot
      if (!this.appointment.appointment_date || !timeSlot) {
        return;
      }
      
      const [hour, minute] = timeSlot.split(':').map(Number);
      
      // Get current selected date
      let selectedDate = this.$moment(this.appointment.appointment_date);
      
      // If ward timezone is set, work in ward timezone
      if (this.wardTimezone && this.$moment.tz) {
        selectedDate = this.$moment.tz(this.appointment.appointment_date, this.wardTimezone);
      }
      
      // Set the time
      selectedDate.hour(hour).minute(minute).second(0).millisecond(0);
      
      // Update the appointment date
      this.isUpdatingDate = true;
      this.appointment.appointment_date = selectedDate.format('YYYY-MM-DDTHH:mm:ssZ');
      
      this.$nextTick(() => {
        this.isUpdatingDate = false;
      });
    },
    async fetchAvailableSlotsForSelectedDay() {
      // Only fetch if we have both date and type selected
      if (!this.appointment.appointment_date || !this.appointment.appointment_type) {
        this.availableSlotsForSelectedDay = [];
        return;
      }
      
      this.loadingAvailableSlots = true;
      
      try {
        const { data } = await AppointmentApi.getAvailableSlotsForDate(
          this.wardId,
          this.publicSecurityKey,
          this.appointment.appointment_date,
          this.appointment.appointment_type
        );
        
        this.availableSlotsForSelectedDay = data.available_slots || [];
      } catch (e) {
        console.error('Error fetching available slots:', e);
        // Fall back to showing configured hours on error
        this.availableSlotsForSelectedDay = [];
      } finally {
        this.loadingAvailableSlots = false;
      }
    },
    async goToStep2() {
      if (!this.appointment.appointment_date) {
        this.errorMessage = "Please select a date and time";
        return;
      }
      
      if (!this.appointment.appointment_type) {
        this.errorMessage = "Please select an appointment type";
        return;
      }
      
      // Check if the selected slot is still available
      this.loading = true;
      this.errorMessage = null;
      
      try {
        const { data } = await AppointmentApi.checkAppointmentAvailability(
          this.wardId,
          this.publicSecurityKey,
          this.appointment.appointment_date,
          this.appointment.appointment_type
        );
        
        if (!data.available) {
          this.errorMessage = data.message || "This appointment slot is no longer available. Please select a different time.";
          this.loading = false;
          return;
        }
        
        // Slot is available, proceed to step 2
        this.errorMessage = null;
        this.currentStep = 2;
      } catch (e) {
        if (e.response && e.response.data && e.response.data.detail) {
          this.errorMessage = e.response.data.detail;
        } else {
          this.errorMessage = "Unable to check availability. Please try again.";
        }
        handleError(e, "Error checking appointment availability");
      } finally {
        this.loading = false;
      }
    },
    goToStep1() {
      this.errorMessage = null;
      this.currentStep = 1;
    },
    onPhoneNumberChange() {
      // Reset contact found state when phone number changes
      this.contactFound = null;
      this.showNameInputs = false;
      this.attendee.first_name = null;
      this.attendee.last_name = null;
      
      // Debounced lookup - will trigger after 500ms of no typing
      if (this.attendee.phone_number && this.attendee.phone_number.length >= 10) {
        this.lookupContactByPhone();
      }
    },
    lookupContactByPhone: _.debounce(async function() {
      if (!this.attendee.phone_number || this.attendee.phone_number.length < 10) {
        return;
      }
      
      this.phoneLookupLoading = true;
      this.phoneLookupError = null;
      
      try {
        const { data } = await AppointmentApi.lookupContactByPhone(
          this.wardId,
          this.publicSecurityKey,
          this.attendee.phone_number
        );
        
        if (data && data.first_name && data.last_name) {
          // Contact found
          this.contactFound = {
            first_name: data.first_name,
            last_name: data.last_name,
            contact_id: data.contact_id
          };
          this.attendee.first_name = data.first_name;
          this.attendee.last_name = data.last_name;
          this.showNameInputs = false;
        } else {
          // Contact not found
          this.contactFound = null;
          this.showNameInputs = true;
        }
      } catch (e) {
        console.error('Error looking up contact:', e);
        this.phoneLookupError = "Error looking up contact. Please enter your name manually.";
        this.contactFound = null;
        this.showNameInputs = true;
      } finally {
        this.phoneLookupLoading = false;
      }
    }, 500),
    clearContactLookup() {
      // Clear the contact lookup and allow manual entry
      this.contactFound = null;
      this.showNameInputs = true;
      this.attendee.first_name = null;
      this.attendee.last_name = null;
    },
    async onSubmit(event) {
      event.preventDefault();
      this.errorMessage = null;
      this.submitting = true;
      
      try {
        // Prepare attendee contact
        const attendeeContact = {
          first_name: this.attendee.first_name,
          last_name: this.attendee.last_name,
          phone_number: this.attendee.phone_number,
          contact_id: this.contactFound ? this.contactFound.contact_id : null
        };
        
        // Convert appointment date to UTC ISO format
        // IMPORTANT: The user selects a time thinking it's in the ward's timezone
        // We need to interpret the selected time as ward local time, then convert to UTC
        let appointmentDateUTC = this.appointment.appointment_date;
        if (appointmentDateUTC) {
          // Parse the date/time - VueCtkDateTimePicker gives us a date in ISO format
          // but we need to interpret the time component as being in the ward's timezone
          let momentDate = this.$moment(appointmentDateUTC);
          if (!momentDate.isValid()) {
            throw new Error("Invalid date format");
          }
          
          // Extract date and time components (these are what the user selected)
          const dateStr = momentDate.format('YYYY-MM-DD');
          const timeStr = momentDate.format('HH:mm:ss');
          
          // If ward timezone is available, interpret the time as ward local time
          if (this.wardTimezone && this.$moment.tz) {
            // Create moment in ward's timezone, then convert to UTC
            momentDate = this.$moment.tz(`${dateStr} ${timeStr}`, 'YYYY-MM-DD HH:mm:ss', this.wardTimezone);
            appointmentDateUTC = momentDate.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
          } else {
            // No ward timezone - interpret as UTC (fallback)
            // This assumes the time selected is already in UTC
            appointmentDateUTC = momentDate.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
          }
          
          console.log('Date conversion:', {
            original: this.appointment.appointment_date,
            dateStr: dateStr,
            timeStr: timeStr,
            wardTimezone: this.wardTimezone,
            utc: appointmentDateUTC
          });
        }
        
        // Prepare appointment payload
        const appointmentPayload = {
          ...this.appointment,
          appointment_date: appointmentDateUTC,
          attendee_contacts: [attendeeContact],
          minutes_duration: 15,
          appointment_location: "church building"
        };
        
        const { data } = await AppointmentApi.createPublicAppointment(
          this.wardId,
          this.publicSecurityKey,
          appointmentPayload
        );
        
        this.successMessage = "Appointment has been submitted. Someone will reach out if there are any questions.";
        this.submitting = false;
        
        // Reset form after success
        setTimeout(() => {
          this.appointment = {
            appointment_date: null,
            minutes_duration: 15,
            comments: null,
            appointment_type: null,
            appointment_other_reason: null,
            appointment_location: "church building",
            attendee_contacts: []
          };
          this.attendee = {
            first_name: null,
            last_name: null,
            phone_number: null
          };
          this.contactFound = null;
          this.showNameInputs = false;
          this.phoneLookupLoading = false;
          this.currentStep = 1;
          this.successMessage = null;
        }, 5000);
      } catch (e) {
        this.submitting = false;
        this.errorMessage = e.response.data?.detail
        handleError(e, "Error trying to process appointment request");
      }
    }
  }
};
</script>
<style scoped>
/* Ensure date picker calendar is not cut off */
.public-appointment-container {
  overflow: visible !important;
  position: relative;
  padding-bottom: 500px; /* Add space at bottom for calendar */
  min-height: calc(100vh - 200px);
}

.b-container {
  overflow: visible !important;
  position: relative;
}

.b-card {
  overflow: visible !important;
  position: relative;
}

/* Wrapper for date picker to allow calendar to escape */
.date-picker-wrapper {
  position: relative;
  overflow: visible !important;
  z-index: 1;
}

/* Ensure the date picker wrapper allows overflow */
::v-deep .VueCtkDateTimePicker {
  position: relative;
  z-index: 1;
}

::v-deep .VueCtkDateTimePicker .field {
  position: relative;
  overflow: visible !important;
}

/* Ensure the calendar dropdown can overflow and appears above everything */
::v-deep .VueCtkDateTimePicker .picker {
  z-index: 9999 !important;
  position: fixed !important;
}

/* Mobile responsive styles for calendar */
@media (max-width: 768px) {
  /* Make date picker input full width on mobile */
  .date-picker-wrapper {
    width: 100% !important;
  }
  
  ::v-deep .VueCtkDateTimePicker {
    width: 100% !important;
  }
  
  ::v-deep .VueCtkDateTimePicker .field {
    width: 100% !important;
  }
  
  /* Stack button below input on mobile */
  .date-picker-wrapper .d-flex {
    flex-direction: column !important;
  }
  
  .date-picker-wrapper .d-flex .ml-2 {
    margin-left: 0 !important;
    margin-top: 0.5rem !important;
    width: 100% !important;
  }
  
  ::v-deep .VueCtkDateTimePicker .picker {
    left: 50% !important;
    transform: translateX(-50%) !important;
    max-width: 95vw !important;
    width: auto !important;
    right: auto !important;
    padding-top: 1.5rem !important;
    padding-bottom: 1rem !important;
  }
  
  /* Add more padding between header and calendar content */
  ::v-deep .VueCtkDateTimePicker .picker .picker__header {
    margin-bottom: 1.5rem !important;
    padding-top: 0.5rem !important;
    padding-bottom: 1rem !important;
    min-height: 3rem !important;
  }
  
  /* Make month navigation buttons larger and easier to tap */
  ::v-deep .VueCtkDateTimePicker .picker .picker__header button,
  ::v-deep .VueCtkDateTimePicker .picker .picker__header .picker__nav--prev,
  ::v-deep .VueCtkDateTimePicker .picker .picker__header .picker__nav--next {
    min-width: 44px !important;
    min-height: 44px !important;
    padding: 0.75rem !important;
    font-size: 1.25rem !important;
    margin: 0 0.5rem !important;
  }
  
  /* Ensure month/year text has enough space */
  ::v-deep .VueCtkDateTimePicker .picker .picker__header .picker__month,
  ::v-deep .VueCtkDateTimePicker .picker .picker__header .picker__year {
    padding: 0.5rem 0.75rem !important;
    font-size: 1.1rem !important;
  }
  
  ::v-deep .VueCtkDateTimePicker .picker .picker__container {
    padding-top: 0.75rem !important;
  }
  
  ::v-deep .VueCtkDateTimePicker .picker .picker__container {
    max-width: 100% !important;
    width: 100% !important;
  }
  
  ::v-deep .VueCtkDateTimePicker .picker .picker__calendar {
    max-width: 100% !important;
    width: 100% !important;
  }
  
  ::v-deep .VueCtkDateTimePicker .picker .picker__calendar table {
    width: 100% !important;
    font-size: 0.85rem !important;
  }
  
  ::v-deep .VueCtkDateTimePicker .picker .picker__time {
    max-width: 100% !important;
    width: 100% !important;
  }
  
  /* Reduce padding on mobile */
  .public-appointment-container {
    padding-bottom: 300px !important;
  }
}

/* Make sure form group doesn't clip */
.b-form-group {
  overflow: visible !important;
  position: relative;
}

/* Ensure b-form-row doesn't clip */
.b-form-row {
  overflow: visible !important;
}

/* Ensure b-col doesn't clip */
.b-col {
  overflow: visible !important;
}
</style>

<style>
/* Global style to ensure picker can escape app container */
#app {
  overflow-y: auto !important;
  overflow-x: visible !important;
}

.main-container {
  overflow: visible !important;
}

#router-view {
  overflow: visible !important;
}

/* Force VueCtkDateTimePicker picker to render at body level with fixed positioning */
body .VueCtkDateTimePicker .picker {
  position: fixed !important;
  z-index: 9999 !important;
}

/* Mobile responsive for body-level picker */
@media (max-width: 768px) {
  body .VueCtkDateTimePicker .picker {
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
    max-width: 95vw !important;
    width: auto !important;
    right: auto !important;
    max-height: 90vh !important;
    overflow-y: auto !important;
    padding-top: 1.5rem !important;
    padding-bottom: 1rem !important;
  }
  
  /* Add more padding between header and calendar content */
  body .VueCtkDateTimePicker .picker .picker__header {
    margin-bottom: 1.5rem !important;
    padding-top: 0.5rem !important;
    padding-bottom: 1rem !important;
    min-height: 3rem !important;
  }
  
  /* Make month navigation buttons larger and easier to tap */
  body .VueCtkDateTimePicker .picker .picker__header button,
  body .VueCtkDateTimePicker .picker .picker__header .picker__nav--prev,
  body .VueCtkDateTimePicker .picker .picker__header .picker__nav--next {
    min-width: 44px !important;
    min-height: 44px !important;
    padding: 0.75rem !important;
    font-size: 1.25rem !important;
    margin: 0 0.5rem !important;
  }
  
  /* Ensure month/year text has enough space */
  body .VueCtkDateTimePicker .picker .picker__header .picker__month,
  body .VueCtkDateTimePicker .picker .picker__header .picker__year {
    padding: 0.5rem 0.75rem !important;
    font-size: 1.1rem !important;
  }
  
  body .VueCtkDateTimePicker .picker .picker__container {
    padding-top: 0.75rem !important;
  }
}

/* Ensure all bootstrap containers allow overflow */
.container,
.container-fluid {
  overflow: visible !important;
}

/* Time slot pills styling */
.time-slot-pill {
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  margin: 0.25rem;
  transition: all 0.2s ease;
  user-select: none;
  font-weight: 500;
}

.time-slot-pill:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.time-slot-pill:active {
  transform: translateY(0);
}

/* Flexbox gap fallback for older browsers */
.gap-2 > * {
  margin: 0.25rem;
}

/* Primary variant (selected) */
.badge-primary.time-slot-pill {
  background-color: #007bff;
  border: 2px solid #007bff;
  color: white;
}

/* Outline variant (not selected) */
.badge-outline-primary.time-slot-pill {
  background-color: white;
  border: 2px solid #007bff;
  color: #007bff;
}

.badge-outline-primary.time-slot-pill:hover {
  background-color: #e7f1ff;
}

/* Make pills more touch-friendly on mobile */
@media (max-width: 768px) {
  .time-slot-pill {
    font-size: 1rem;
    padding: 0.6rem 1.2rem;
    margin: 0.3rem;
    min-width: 80px;
    text-align: center;
  }
}
</style>
